import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Select, { SelectOption } from '@amzn/meridian/select';
import { adminApi, PolicySummary } from '../service/adminApiService';
import { SearchInput } from '../components/SearchInput';
import { FilterBar } from '../components/FilterBar';
import { PaginationControls } from '../components/PaginationControls';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/ToastProvider';
import { ALL_OPERATIONS } from '../components/OperationCheckboxGroup';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const GroupsListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [items, setItems] = useState<PolicySummary[]>([]);
  const [tokens, setTokens] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PolicySummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const search = params.get('search') || '';
  const department = params.get('department') || '';
  const prodAccess = params.get('prod_access') || '';
  const operation = params.get('operation') || '';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
      setPageIndex(0);
      setTokens([null]);
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams({}, { replace: true });
    setPageIndex(0);
    setTokens([null]);
  }, [setParams]);

  const fetchPage = useCallback(
    async (tokenIndex: number) => {
      setLoading(true);
      setError(null);
      try {
        const token = tokens[tokenIndex] || undefined;
        const response = await adminApi.listGroups({
          next_token: token,
          search: search || undefined,
          department: department || undefined,
          prod_access: prodAccess || undefined,
          operation: operation || undefined,
        });
        setItems(response.items);
        setNextToken(response.next_token);
      } catch (err: any) {
        setError(err?.message || 'Failed to load groups');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tokens, search, department, prodAccess, operation],
  );

  useEffect(() => {
    fetchPage(pageIndex);
  }, [fetchPage, pageIndex]);

  const activeCount = useMemo(
    () =>
      [search, department, prodAccess, operation].filter((v) => v && v.length > 0).length,
    [search, department, prodAccess, operation],
  );

  const onNext = () => {
    if (!nextToken) return;
    const newTokens = [...tokens];
    newTokens[pageIndex + 1] = nextToken;
    setTokens(newTokens);
    setPageIndex(pageIndex + 1);
  };

  const onPrev = () => {
    if (pageIndex === 0) return;
    setPageIndex(pageIndex - 1);
  };

  const performDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteGroup(pendingDelete.posix_group);
      toast.push({ type: 'success', message: `Deleted group ${pendingDelete.posix_group}` });
      setPendingDelete(null);
      fetchPage(pageIndex);
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Failed to delete group' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Column spacing="large">
      <PageHeader
        title="Groups"
        subtitle={`${items.length} group${items.length === 1 ? '' : 's'} shown`}
        actions={
          <>
            <Button
              type="tertiary"
              onClick={async () => {
                try {
                  await adminApi.exportGroups();
                  toast.push({ type: 'success', message: 'Downloaded rbac-groups.csv' });
                } catch (err: any) {
                  toast.push({ type: 'error', message: err?.message || 'Export failed' });
                }
              }}
            >
              Download CSV
            </Button>
            <Button type="primary" onClick={() => navigate('/admin/groups/new')}>
              + New group
            </Button>
          </>
        }
      />

      <Section>
      <FilterBar activeCount={activeCount} onClear={clearFilters}>
        <div style={{ minWidth: 220 }}>
          <SearchInput
            value={search}
            onChange={(next) => setParam('search', next || null)}
            placeholder="Search posix_group…"
            ariaLabel="Search groups"
          />
        </div>
        <Select
          value={prodAccess}
          onChange={(next: string) => setParam('prod_access', next || null)}
          placeholder="Prod access: any"
          aria-label="Filter by production access"
          width={180}
        >
          <SelectOption value="" label="Any" />
          <SelectOption value="true" label="Yes" />
          <SelectOption value="false" label="No" />
        </Select>
        <Select
          value={operation}
          onChange={(next: string) => setParam('operation', next || null)}
          placeholder="Operation: any"
          aria-label="Filter by operation"
          width={220}
        >
          <SelectOption value="" label="Any" />
          {ALL_OPERATIONS.map((op) => (
            <SelectOption key={op.id} value={op.id} label={op.label} />
          ))}
        </Select>
        <div style={{ minWidth: 180 }}>
          <SearchInput
            value={department}
            onChange={(next) => setParam('department', next || null)}
            placeholder="Department exact match"
            ariaLabel="Filter by department"
          />
        </div>
      </FilterBar>

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader type="circular" size="large" />
        </Row>
      ) : items.length === 0 ? (
        <Alert type="neutral">No groups match the current filters.</Alert>
      ) : (
        <Table headerRows={1} showDividers={true} aria-label="RBAC groups">
          <TableRow>
            <TableCell>POSIX group</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Operations</TableCell>
            <TableCell>Accounts</TableCell>
            <TableCell>Prod</TableCell>
            <TableCell>Last updated</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
          {items.map((g) => (
            <TableRow key={g.posix_group}>
              <TableCell>
                <Link to={`/admin/groups/${encodeURIComponent(g.posix_group)}`}>
                  {g.posix_group}
                </Link>
              </TableCell>
              <TableCell>{g.department || '-'}</TableCell>
              <TableCell>{g.operation_count}</TableCell>
              <TableCell>{g.account_count}</TableCell>
              <TableCell>{g.prod_access ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                <Text type="b200">{g.last_updated?.slice(0, 19) || '-'}</Text>
              </TableCell>
              <TableCell>
                <Row spacing="xsmall">
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() =>
                      navigate(`/admin/groups/${encodeURIComponent(g.posix_group)}/edit`)
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() =>
                      navigate(
                        `/admin/groups/new?clone_from=${encodeURIComponent(g.posix_group)}`,
                      )
                    }
                  >
                    Clone
                  </Button>
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() => setPendingDelete(g)}
                  >
                    Delete
                  </Button>
                </Row>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}

      <PaginationControls
        currentIndex={pageIndex}
        hasPrevious={pageIndex > 0}
        hasNext={!!nextToken}
        onPrevious={onPrev}
        onNext={onNext}
        itemCount={items.length}
      />
      </Section>

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete group ${pendingDelete?.posix_group || ''}?`}
        message={
          <Column spacing="xsmall">
            <Text>This removes the policy record. Users currently in the group will lose the
              access granted here as soon as their Group Resolver cache refreshes (within 5 minutes).
            </Text>
            <Text type="b200" color="secondary">
              The action is auditable and reversible by creating the group again with the same name.
            </Text>
          </Column>
        }
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onConfirm={performDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Column>
  );
};
